import React from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper,
} from '@mui/material';
import { AcceptanceBadge, fmt, fmtPct } from './msaFormat';

// ---------------------------------------------------------------------------
// AttributeAgreementResults — renders the "attribute" (Attribute Agreement
// Analysis) engine payload: within-appraiser and between-appraiser kappa +
// %agreement, plus (only when a known standard was supplied) appraiser-vs-
// standard agreement and per-operator confusion matrices.
//
// `results` schema:
//   withinAppraiser   : { [op]: { agreementPercent, kappa|null, kappaCI:{lower,upper} } }
//   betweenAppraiser  : { agreementPercent, fleissKappa|null, kappaCI:{lower,upper} }
//   appraiserVsStandard?     : { [op]: { agreementPercent, kappa|null (Cohen's), kappaCI, fleissKappa|null (AIAG/JASP) } }
//   allAppraisersVsStandard? : { agreementPercent, cohenKappa|null, fleissKappa|null (AIAG/JASP) }
//   confusionMatrices : { [op]: number[][] }   // rows=standard, cols=appraiser
//   acceptance        : { overall: 'acceptable'|'marginal'|'unacceptable' }
// `meta.categories` gives the category order for confusion-matrix axis labels.
// ---------------------------------------------------------------------------

const headCellSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    py: 1,
    px: 1.5,
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.85rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 0.75,
    px: 1.5,
    whiteSpace: 'nowrap',
};

const sectionLabelSx = {
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 600,
    fontSize: '0.9rem',
    mb: 1,
};

// Format a { lower, upper } kappa CI as "l..u" (or '—' when either bound missing).
function fmtCI(ci) {
    if (!ci) return '—';
    const lo = ci.lower;
    const hi = ci.upper;
    if (lo === null || lo === undefined || !isFinite(lo)
        || hi === null || hi === undefined || !isFinite(hi)) {
        return '—';
    }
    return `${fmt(lo, 4)}..${fmt(hi, 4)}`;
}

// Stable, human-friendly operator ordering: Op1, Op2 … numerically where possible.
function sortedOps(obj) {
    const keys = Object.keys(obj || {});
    return keys.sort((a, b) => {
        const na = parseInt(String(a).replace(/\D/g, ''), 10);
        const nb = parseInt(String(b).replace(/\D/g, ''), 10);
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
        return String(a).localeCompare(String(b));
    });
}

function KappaTable({ title, dataByOp }) {
    const ops = sortedOps(dataByOp);
    // The vs-standard tables also carry the AIAG/JASP Fleiss' κ; within-appraiser does not.
    const hasFleiss = ops.some((op) => dataByOp[op] && dataByOp[op].fleissKappa != null);
    return (
        <Box>
            <Typography sx={sectionLabelSx}>{title}</Typography>
            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    overflowX: 'auto',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headCellSx}>Operator</TableCell>
                            <TableCell sx={headCellSx} align="right">%Agreement</TableCell>
                            <TableCell sx={headCellSx} align="right">Cohen's κ</TableCell>
                            {hasFleiss && <TableCell sx={headCellSx} align="right">Fleiss' κ</TableCell>}
                            {/* CI is always the Cohen's-κ interval; label it explicitly when the Fleiss column is present so it isn't misread as the Fleiss CI (L14). */}
                            <TableCell sx={headCellSx} align="right">{hasFleiss ? "Cohen 95% CI" : '95% CI'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ops.map((op) => {
                            const d = dataByOp[op] || {};
                            return (
                                <TableRow key={op}>
                                    <TableCell sx={{ ...bodyCellSx, color: '#90caf9', fontWeight: 600 }}>{op}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{fmtPct(d.agreementPercent, 1)}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{fmt(d.kappa, 4)}</TableCell>
                                    {hasFleiss && <TableCell sx={bodyCellSx} align="right">{fmt(d.fleissKappa, 4)}</TableCell>}
                                    <TableCell sx={bodyCellSx} align="right">{fmtCI(d.kappaCI)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function StatLine({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{label}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', fontWeight: 600 }}>{value}</Typography>
        </Box>
    );
}

function ConfusionMatrix({ op, matrix, categories }) {
    const cats = Array.isArray(categories) && categories.length ? categories : null;
    const rows = Array.isArray(matrix) ? matrix : [];
    const size = rows.length;
    // Axis labels: fall back to numeric indices when meta.categories is absent
    // or mismatched in length.
    const labels = cats && cats.length === size
        ? cats
        : Array.from({ length: size }, (_, i) => `C${i + 1}`);

    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#90caf9', fontWeight: 600, fontSize: '0.82rem', mb: 0.75 }}>
                {op}
            </Typography>
            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'inline-block',
                    overflowX: 'auto',
                    maxWidth: '100%',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, color: 'rgba(255,255,255,0.4)' }}>
                                Std ＼ App
                            </TableCell>
                            {labels.map((lab) => (
                                <TableCell key={lab} sx={headCellSx} align="right">{lab}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, r) => (
                            <TableRow key={labels[r] || r}>
                                <TableCell sx={{ ...bodyCellSx, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
                                    {labels[r] || `C${r + 1}`}
                                </TableCell>
                                {(Array.isArray(row) ? row : []).map((cell, c) => (
                                    <TableCell
                                        key={c}
                                        sx={{
                                            ...bodyCellSx,
                                            color: r === c ? '#81c784' : 'rgba(255,255,255,0.85)',
                                            fontWeight: r === c ? 600 : 400,
                                        }}
                                        align="right"
                                    >
                                        {Number.isFinite(cell) ? cell : 0}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function AttributeAgreementResults({ results, meta }) {
    if (!results) return null;

    const {
        withinAppraiser = {},
        betweenAppraiser = {},
        appraiserVsStandard,
        allAppraisersVsStandard,
        confusionMatrices = {},
        acceptance = {},
    } = results;

    const categories = meta?.categories;
    const hasStandard = Boolean(appraiserVsStandard);
    const stdOps = hasStandard ? sortedOps(confusionMatrices) : [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Headline acceptance */}
            <Box>
                <AcceptanceBadge status={acceptance.overall} label="Agreement" />
            </Box>

            {/* Within-appraiser */}
            <KappaTable title="Within-Appraiser (Repeatability)" dataByOp={withinAppraiser} />

            {/* Between-appraiser */}
            <Box>
                <Typography sx={sectionLabelSx}>Between-Appraiser (Reproducibility)</Typography>
                <Paper
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        p: 2,
                        maxWidth: 420,
                    }}
                >
                    <StatLine label="%Agreement" value={fmtPct(betweenAppraiser.agreementPercent, 1)} />
                    <StatLine label="Fleiss Kappa" value={fmt(betweenAppraiser.fleissKappa, 4)} />
                    <StatLine label="95% CI" value={fmtCI(betweenAppraiser.kappaCI)} />
                </Paper>
            </Box>

            {/* Appraiser vs standard (only when a known standard was supplied) */}
            {hasStandard && (
                <>
                    <KappaTable title="Each Appraiser vs Standard" dataByOp={appraiserVsStandard} />

                    {allAppraisersVsStandard && (
                        <Box>
                            <Typography sx={sectionLabelSx}>All Appraisers vs Standard</Typography>
                            <Paper
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.02)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    p: 2,
                                    maxWidth: 420,
                                }}
                            >
                                <StatLine label="%Agreement" value={fmtPct(allAppraisersVsStandard.agreementPercent, 1)} />
                                <StatLine label="Cohen's κ" value={fmt(allAppraisersVsStandard.cohenKappa, 4)} />
                                <StatLine label="Fleiss' κ" value={fmt(allAppraisersVsStandard.fleissKappa, 4)} />
                            </Paper>
                        </Box>
                    )}

                    {stdOps.length > 0 && (
                        <Box>
                            <Typography sx={sectionLabelSx}>Confusion Matrices (rows = Standard, cols = Appraiser)</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                {stdOps.map((op) => (
                                    <ConfusionMatrix
                                        key={op}
                                        op={op}
                                        matrix={confusionMatrices[op]}
                                        categories={categories}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Interpretation hint + method legend */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                    Kappa interpretation: &gt; 0.75 good agreement · 0.4–0.75 marginal · &lt; 0.4 poor.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                    Method &amp; sources: within/between use <b>Cohen's κ</b> [Cohen 1960] / <b>Fleiss' κ</b> [Fleiss 1971].
                    The vs-standard tables report <b>both</b>: Cohen's κ (classic 2×2, = JASP <i>psych::cohen.kappa</i>) and
                    Fleiss' κ (AIAG / JASP <i>irr::kappam.fleiss</i>, per-trial averaged). Our Fleiss' κ matches Minitab's
                    Attribute Agreement output exactly; Minitab's separate "Cohen's" column uses a different
                    expected-agreement convention. Ref: AIAG MSA Reference Manual (4th ed.); JASP jaspQualityControl.
                </Typography>
            </Box>
        </Box>
    );
}

export default AttributeAgreementResults;
